<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Questão de Avaliação' : 'Nova Questão de Avaliação' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarQuestao" @cancel="voltar">
      <FormSection title="Informações da Questão">
        <!-- Primeira Linha: Categoria, Bloco, Tipo de Dado e Ativo -->
        <div class="d-flex flex-wrap gap-3 mb-3">
          <!-- Categoria de Avaliação -->
          <div class="flex-fill larger-field">
            <SelectField
              id="id_categoria"
              v-model="questao.id_categoria"
              :options="categoriasOptions"
              placeholder="Selecione a categoria"
              label="Categoria de Avaliação <span class='required'>*</span>"
              required
              @update:modelValue="onCategoriaChange"
            />
            <div v-if="errors.id_categoria" class="error-message">{{ errors.id_categoria }}</div>
          </div>
          <!-- Bloco da Questão -->
          <div class="flex-fill larger-field">
            <SelectField
              id="id_bloco"
              v-model="questao.id_bloco"
              :options="blocosOptions"
              :optionDisabled="isBlocoDisabled"
              placeholder="Selecione o bloco"
              label="Bloco da Questão <span class='required'>*</span>"
              required
            />
            <div v-if="errors.id_bloco" class="error-message">{{ errors.id_bloco }}</div>
          </div>
          <!-- Tipo de Dado -->
          <div class="flex-fill larger-field">
            <SelectField
              id="tipo_dado"
              v-model="questao.tipo_dado"
              :options="tipoDadoOptions"
              placeholder="Selecione o tipo de dado"
              label="Tipo de Dado <span class='required'>*</span>"
              required
            />
          <div v-if="errors.tipo_dado" class="error-message">{{ errors.tipo_dado }}</div>

          <!-- para Numérico ou Múltipla seleção -->
          <div v-if="['Numérico','Múltipla seleção'].includes(questao.tipo_dado)" class="mb-3">
            <InputField
              id="pontuacao"
              type="text"
              v-model="pontuacaoStr"
              @update:modelValue="onPontuacaoInput"
              @blur="onPontuacaoBlur"
              placeholder="Ex.: 3,5"
              label="Pontuação <span class='required'>*</span>"
              required
              :class="{ 'error-input': errors.pontuacao }"
              style="width: 200px;"
            />
            <div v-if="errors.pontuacao" class="error-message">{{ errors.pontuacao }}</div>
          </div>

          </div>
          <!-- Ativo -->
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <div class="form-switch">
                <input
                  type="checkbox"
                  id="status"
                  v-model="questao.ativo"
                  class="form-check-input"
                />
                <label for="status" class="form-check-label">
                  {{ questao.ativo ? "Ativo" : "Inativo" }}
                </label>
              </div>
            </div>
        </div>

        <!-- Segunda Linha: Campo Questão com largura 100% -->
        <div class="mb-3">
          <InputField
            id="questao"
            v-model="questao.questao"
            placeholder="Digite a questão"
            label="Questão <span class='required'>*</span>"
            required
            :class="{ 'error-input': errors.questao }"
            style="width: 700px;"
          />
          <div v-if="errors.questao" class="error-message">{{ errors.questao }}</div>
        </div>
      </FormSection>

      <!-- ==================== Opções inline da Questão ==================== -->
      <FormSection
        v-if="['Lista','Múltipla seleção','Numérico'].includes(questao.tipo_dado)"
        title="Opções / Respostas da Questão"
      >
        <!-- Botão adicionar -->
        <div class="d-flex justify-content-end mb-2">
          <button type="button" class="btn btn-sm btn-primary" @click="addOpcao">
            <i class="bi bi-plus-circle me-1"></i> Adicionar
          </button>
        </div>

        <!-- Tabela/Lista de opções -->
        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <!-- Para Lista/Múltipla -->
              <template v-if="questao.tipo_dado !== 'Numérico'">
                <tr>
                  <th style="width: 400px">Opção</th>
                  <!-- Mostra 'Correta?' só em Múltipla seleção -->
                  <th v-if="questao.tipo_dado === 'Múltipla seleção'" style="width: 100px">Correta?</th>
                  <th style="width: 200">Observação</th>
                </tr>
              </template>

              <!-- Para Numérico -->
              <template v-else>
                <tr>
                  <th style="width: 65%">Resposta</th>
                  <th style="width: 20%">Pontuação</th>
                  <th style="width: 35%">Observação</th>
                </tr>
              </template>
            </thead>

            <tbody>
              <!-- Lista / Múltipla -->
              <template v-if="questao.tipo_dado !== 'Numérico'">
                <tr v-for="(row, idx) in opcoes" :key="row.id ?? `opt_${idx}`">
                  <td>
                    <InputField
                      :id="`opcao_${idx}`"
                      v-model="row.opcao"
                      :label="''"
                      placeholder="Digite a opção"
                      style="width:100%"
                    />
                  </td>

                  <!-- Select 'Correta?' só quando for Múltipla seleção -->
                  <td v-if="questao.tipo_dado === 'Múltipla seleção'">
                    <SelectField
                      :id="`correta_${idx}`"
                      v-model="row.correta"
                      :options="[{value:'Sim',text:'Sim'},{value:'Não',text:'Não'}]"
                      :label="''"
                      placeholder="Correta?"
                    />
                  </td>

                  <td>
                    <InputField
                      :id="`obs_${idx}`"
                      v-model="row.observacao"
                      :label="''"
                      placeholder="Observação (opcional)"
                      style="width:100%"
                    />
                  </td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="removeOpcao(idx)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </template>

              <!-- Numérico (mantém igual ao seu) -->
              <template v-else>
                <tr v-for="(row, idx) in opcoes" :key="row.id ?? `num_${idx}`">
                  <td>
                    <InputField
                      :id="`valor_${idx}`"
                      v-model="row.opcao"
                      :label="''"
                      placeholder=""
                      style="width:100%"
                    />
                  </td>
                  <td>
                    <InputField
                      :id="`pont_${idx}`"
                      type="text"
                      v-model="row.pontuacao"
                      placeholder="Ex.: 2,5 ou 2.5"
                      :label="''"
                      style="width:100%"
                    />
                  </td>
                  <td>
                    <InputField
                      :id="`obsn_${idx}`"
                      v-model="row.observacao"
                      :label="''"
                      placeholder="Observação (opcional)"
                      style="width:100%"
                    />
                  </td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="removeOpcao(idx)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </template>

              <tr v-if="opcoes.length === 0">
                <td :colspan="4" class="text-muted">
                  Nenhuma opção adicionada. Clique em <strong>Adicionar</strong>.
                </td>
              </tr>
            </tbody>

          </table>
        </div>

        <!-- Erros -->
        <div v-if="errors.opcoes" class="error-message mt-1">{{ errors.opcoes }}</div>
      </FormSection>

    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const questao = reactive({
      id: null,
      id_categoria: "",
      id_bloco: "",
      questao: "",
      tipo_dado: "",
      ativo: true,
      pontuacao: null
    });

    const errors = reactive({});
    const isSubmitting = ref(false);

    const norm = (s) => String(s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

    const isCategoriaDesempenho = computed(() => {
      const cat = categorias.value.find(c => Number(c.id) === Number(questao.id_categoria));
      return norm(cat?.categoria) === norm('Avaliação de Desempenho');
    });

    const requiresPontuacao = computed(() =>
      ["Numérico", "Múltipla seleção"].includes(questao.tipo_dado)
    );

    const pontuacaoStr = ref('') // string visível no input

    const toNumberFromBR = (s) => {
      if (s == null) return null;
      const cleaned = String(s).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
      const n = Number.parseFloat(cleaned);
      return Number.isFinite(n) ? n : null;
    };

    const toBR = (n) => {
      if (n == null || !Number.isFinite(Number(n))) return '';
      return Number(n).toFixed(2).replace('.', ',');
    };

    // chamado a cada digitação (mantém vírgula na tela e atualiza number)
    const onPontuacaoInput = (val) => {
      // mantém apenas dígitos e uma vírgula
      let s = String(val ?? '').replace(/[^\d,]/g, '');
      // evita mais de uma vírgula
      const parts = s.split(',');
      if (parts.length > 2) s = parts[0] + ',' + parts.slice(1).join('').replace(/,/g,'');
      pontuacaoStr.value = s;

      const num = toNumberFromBR(s);
      questao.pontuacao = num; // number para salvar/validar
    };

    // opcional: ao sair do campo, formata "certinho" com 2 casas
    const onPontuacaoBlur = () => {
      pontuacaoStr.value = toBR(questao.pontuacao);
    };

    // IDs de blocos já usados na categoria escolhida
    const usedBlocoIds = ref(new Set());

    // busca questões da categoria e constrói o set de blocos usados
    const fetchBlocosUsadosPorCategoria = async (idCategoria) => {
      usedBlocoIds.value = new Set();
      if (!idCategoria) return;

      try {
        const resp = await axios.get('/configuracoes/parametros-avaliacoes/questoes/todos', {
          params: { id_categoria: Number(idCategoria) }
        });

        const lista = Array.isArray(resp?.data?.data) ? resp.data.data : (resp.data || []);
        const idsUsados = lista
          .map(q => Number(q?.id_bloco))
          .filter(id => Number.isFinite(id));

        const blocoAtual = Number(questao.id_bloco || NaN);
        const permitirAtual = route.params.id && Number.isFinite(blocoAtual);

        const novoSet = new Set(permitirAtual ? idsUsados.filter(id => id !== blocoAtual) : idsUsados);
        usedBlocoIds.value = novoSet;

      } catch (e) {
        console.error('Erro ao buscar blocos usados:', e);
        usedBlocoIds.value = new Set();
      }
    };

    // Listas para categorias e blocos
    const categorias = ref([]);
    const blocos = ref([]);

    const categoriasOptions = computed(() =>
      categorias.value.map(cat => ({
        value: cat.id,
        text: cat.categoria,
      }))
    );

    const blocosOptions = computed(() => {
      const todos = (blocos.value || []).map(b => ({
        value: Number(b.id),
        text: b.bloco,
      }));

      return todos.map(opt => {
        const idNum = Number(opt.value);
        const disabled = isCategoriaDesempenho.value && usedBlocoIds.value.has(idNum);
        return { ...opt, disabled };
      });
    });

    const isBlocoDisabled = (id) => {
      const idNum = Number(id);
      // Em edição: nunca desabilite o bloco já selecionado
      if (Number(questao.id_bloco) === idNum) return false;

      // Só desabilita se a categoria for "Avaliação de Desempenho"
      if (!isCategoriaDesempenho.value) return false;

      return isCategoriaDesempenho.value && usedBlocoIds.value.has(idNum);
    };

    
    // Opções fixas para Tipo de Dado
    const tipoDadoOptions = [
      { value: "Lista", text: "Lista" },
      { value: "Múltipla seleção", text: "Múltipla seleção" },
      { value: "Numérico", text: "Numérico" },
      // { value: "Booleano", text: "Booleano" },
      // { value: "Texto", text: "Texto" },
    ];

    // === Opções inline da questão ===
    const opcoes = ref([])
    /*
      Estrutura por linha:
      {
        // comum
        id: null | number,          // se vier do banco, manter para saber o que atualizar
        opcao: '',                  // texto da opção (Lista/Múltipla) OU valor numérico em string (Numérico)
        observacao: '',
        // específicos
        correta: 'Não',             // 'Sim'|'Não'|'N/A' (Lista/Múltipla usam 'Sim'/'Não'; Numérico usa sempre 'N/A')
        pontuacao: null             // usado só em 'Numérico'
      }
    */

    // adiciona uma linha padrão conforme tipo
    const addOpcao = () => {
      if (questao.tipo_dado === 'Numérico') {
        opcoes.value.push({ id: null, opcao: '', observacao: '', correta: 'N/A', pontuacao: null })
      } else if (questao.tipo_dado === 'Lista') {
        opcoes.value.push({ id: null, opcao: '', observacao: '', correta: 'N/A', pontuacao: null })
      } else {
        // Múltipla seleção
        opcoes.value.push({ id: null, opcao: '', observacao: '', correta: 'Sim',  pontuacao: null })
      }
    }

    // remove
    const removeOpcao = (idx) => {
      opcoes.value.splice(idx, 1)
    }

    // normaliza número (para pontuação/resposta numérica)
    const normNum = (s) => {
      if (s == null) return ''
      const cleaned = String(s).replace(/[^\d,.-]/g, '').replace(/\.(?=.*\.)/g,'') // remove pontos extras
      return cleaned.replace(/\./g,'').replace(',', '.')
    }


    // Busca as categorias de avaliação
    const fetchCategorias = async () => {
      try {
        let resp;
        try {
          resp = await axios.get('/configuracoes/parametros-avaliacoes/categorias', {
            params: { limit: 'all', sortField: 'nome', sortOrder: 'asc' }
          });
        } catch {
          resp = await axios.get('/configuracoes/parametros-avaliacoes/categorias', {
            params: { page: 1, limit: 10000, sortField: 'nome', sortOrder: 'asc' }
          });
        }

        const lista = Array.isArray(resp?.data?.data)
          ? resp.data.data
          : (Array.isArray(resp?.data) ? resp.data : []);

        // ⬇️ NORMALIZAR AQUI: garantir {id, categoria}
        const normalizadas = (lista || []).map(c => ({
          id: Number(c.id),
          categoria: c.categoria ?? c.nome ?? '',  // pega 'categoria' ou 'nome'
          ativo: c.ativo !== false
        }));

        // ordenar por 'categoria' (já normalizada)
        normalizadas.sort((a, b) =>
          a.categoria.localeCompare(b.categoria, 'pt-BR', { sensitivity: 'base', numeric: true })
        );

        categorias.value = normalizadas;
        console.debug('[fetchCategorias] normalizadas:', categorias.value);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        categorias.value = [];
      }
    };

    // Busca os blocos e filtra os que pertencem à categoria selecionada
    const fetchBlocos = async (id_categoria) => {
      try {
        const response = await axios.get("/configuracoes/parametros-avaliacoes/blocos/todos", {
          params: { id_categoria: Number(id_categoria) }
        });

        const catIdNum = Number(id_categoria);
        const blocosFiltrados = (response.data.data || response.data || [])
          .filter(b => Number(b.id_categoria) === catIdNum);

        blocos.value = blocosFiltrados;
      } catch (error) {
        console.error("Erro ao carregar blocos:", error);
      }
    };

    // Quando a categoria muda, limpa o bloco selecionado e carrega os blocos relacionados
    const onCategoriaChange = async (newCategoriaId) => {
      questao.id_bloco = "";
      const idNum = Number(newCategoriaId);
      if (Number.isFinite(idNum)) {
        await fetchBlocos(idNum);
        await fetchBlocosUsadosPorCategoria(idNum);
      } else {
        blocos.value = [];
        usedBlocoIds.value = new Set();
      }
    };

    const validateForm = () => {
      errors.id_categoria = questao.id_categoria ? "" : "A categoria é obrigatória.";
      errors.id_bloco     = questao.id_bloco     ? "" : "O bloco é obrigatório.";
      errors.questao      = questao.questao.trim() ? "" : "A questão é obrigatória.";
      errors.tipo_dado    = questao.tipo_dado    ? "" : "O tipo de dado é obrigatório.";

      if (requiresPontuacao.value) {
        errors.pontuacao =
          questao.pontuacao !== null && Number.isFinite(questao.pontuacao)
            ? ""
            : "A pontuação é obrigatória para este tipo de dado.";
      } else {
        errors.pontuacao = "";
      }
      // === Validação das opções inline ===
      if (questao.tipo_dado === 'Lista') {
        const preenchidas = opcoes.value.filter(o => String(o.opcao || '').trim() !== '')
        errors.opcoes = ''
        if (preenchidas.length < 2) errors.opcoes = 'Inclua ao menos 2 opções.'
      }
      else if (questao.tipo_dado === 'Múltipla seleção') {
        // ao menos 2 opções e pelo menos 1 correta
        const preenchidas = opcoes.value.filter(o => String(o.opcao).trim() !== '')
        const corretas = opcoes.value.filter(o => o.correta === 'Sim')
        errors.opcoes = ''
        if (preenchidas.length < 2) errors.opcoes = 'Inclua ao menos 2 opções.'
        else if (corretas.length < 1) errors.opcoes = 'Selecione ao menos 1 opção correta.'
      }
      else if (questao.tipo_dado === 'Numérico') {
        errors.opcoes = ''
        if (opcoes.value.length < 1) {
          errors.opcoes = 'Inclua ao menos 1 linha.'
        } else {
          for (const o of opcoes.value) {
            // Resposta agora é TEXTO: só exigir preenchido
            const respRaw = String(o.opcao ?? '').trim()
            if (!respRaw) { errors.opcoes = 'Preencha o campo "Resposta".'; break }
          }
        }
      }
    };

    const isFormValid = computed(() => {
      const basicValid =
        questao.id_categoria !== "" &&
        questao.id_bloco !== "" &&
        questao.questao.trim() !== "" &&
        questao.tipo_dado !== "";

      const pontuacaoValid = !requiresPontuacao.value
        ? true
        : questao.pontuacao !== null && Number.isFinite(questao.pontuacao);

      return basicValid && pontuacaoValid && Object.values(errors).every((e) => e === "");
    });

    const novoQuestao = () => {
      Object.assign(questao, {
        id_categoria: "",
        id_bloco: "",
        questao: "",
        tipo_dado: "",
        ativo: true,
      });
      for (const key in errors) {
        if (Object.hasOwn(errors, key)) errors[key] = "";
      }
    };

    const toIntOrNull = (v) => (v === '' || v == null ? null : parseInt(v, 10))

    const salvarQuestao = async () => {
      // MONTA O PAYLOAD AGORA, COM VALORES ATUAIS
      const payload = {
        id_categoria: toIntOrNull(questao.id_categoria),
        id_bloco:     toIntOrNull(questao.id_bloco),
        questao:      questao.questao,
        tipo_dado:    questao.tipo_dado,
        ativo:        questao.ativo,
        pontuacao:    requiresPontuacao.value ? questao.pontuacao : null,
        opcoes: opcoes.value.map(o => {
          const isNum   = questao.tipo_dado === 'Numérico'
          const isLista = questao.tipo_dado === 'Lista'
          return {
            id: (o.id === '' || o.id == null) ? null : Number(o.id),
            opcao: String(o.opcao ?? ''),
            correta: (isNum || isLista) ? 'N/A' : (o.correta === 'Sim' ? 'Sim' : 'Não'),
            observacao: o.observacao ?? '',
            pontuacao: isNum ? (Number(normNum(o.pontuacao)) || 0) : null
          }
        })
      }

      if (isSubmitting.value) return;
      validateForm();
      if (!isFormValid.value) return;

      isSubmitting.value = true;
      try {
        const method = questao.id ? "put" : "post";
        const url = questao.id
          ? `/configuracoes/parametros-avaliacoes/questoes/${questao.id}`
          : `/configuracoes/parametros-avaliacoes/questoes`;

        await axios[method](url, payload)
        router.push("/configuracoes/parametros-avaliacoes/questoes")
      } catch (error) {
        console.error("Erro ao salvar questão:", error);
        const msg = error?.response?.data?.error || error?.message || "Erro desconhecido";
        console.error("Detalhe do backend:", msg);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/configuracoes/parametros-avaliacoes/questoes");
    };

    watch(() => questao.id_bloco, (novo, antigo) => {
      // Só bloquear duplicata quando a categoria for "Avaliação de Desempenho"
      if (!isCategoriaDesempenho.value) return;
      const id = Number(novo);
      if (usedBlocoIds.value.has(id)) {
        // volta para o valor anterior (ou vazio)
        questao.id_bloco = antigo ?? "";
        console.warn('Este bloco já está utilizado por outra questão desta categoria.');
      }
    });

    watch(() => questao.pontuacao, (val) => {
      if (val === null || val === '') return;
      const normalizado = String(val).replace(/[^\d,.-]/g, '').replace(',', '.');
      const num = parseFloat(normalizado);
      if (!isNaN(num)) {
        questao.pontuacao = num;
      }
    });


    // Quando o tipo muda, zera o array para o formato correto (evita lixo de outro tipo)
    watch(() => questao.tipo_dado, (tipo) => {
      // zera erros e array
      errors.opcoes = ''
      opcoes.value = []
      if (!tipo) return

      // NOVO: comportamento especial para Lista em NOVO registro
      const isNovo = !route.params.id && !questao.id

      if (tipo === 'Lista') {
        // preenche o título automaticamente (apenas se novo e se ainda está vazio)
        if (isNovo && (!questao.questao || !questao.questao.trim())) {
          questao.questao = 'Avaliação'
        }
        // pré-carrega 3 opções padrão
        opcoes.value = [
          { id: null, opcao: 'Bom',        observacao: '', correta: 'N/A', pontuacao: null },
          { id: null, opcao: 'Muito Bom',  observacao: '', correta: 'N/A', pontuacao: null },
          { id: null, opcao: 'Excelente',  observacao: '', correta: 'N/A', pontuacao: null },
        ]
        return
      }

      if (tipo === 'Numérico') {
        // mantém comportamento atual: 1 linha vazia
        addOpcao()
        return
      }

      // Múltipla seleção: mantenha 2 linhas vazias
      addOpcao()
      addOpcao()
    })

    // Se estiver editando, além da questão, buscar as opções existentes
    const fetchOpcoesDaQuestao = async (questaoId) => {
      try {
        // use o endpoint que você já tiver disponível; exemplos comuns:
        // const resp = await axios.get(`/configuracoes/parametros-avaliacoes/opcoes?questao_id=${questaoId}`)
        // ou:
        // const resp = await axios.get(`/configuracoes/parametros-avaliacoes/questoes/${questaoId}/opcoes`)
        const resp = await axios.get(`/configuracoes/parametros-avaliacoes/questoes/${questaoId}/opcoes`)
        const lista = Array.isArray(resp?.data?.data) ? resp.data.data : (resp.data || [])
        opcoes.value = lista.map(o => ({
          id: o.id ?? null,
          opcao: String(o.opcao ?? ''),
          observacao: o.observacao ?? '',
          correta: (o.correta === 'Sim' || o.correta === 'Não') ? o.correta : 'N/A',
          pontuacao: o.pontuacao != null ? toBR(Number(o.pontuacao)) : ''
        }))
      } catch (e) {
        console.error('Erro ao carregar opções da questão:', e)
        opcoes.value = []
      }
    }

    // Quando trocar para tipo sem pontuação, zere o valor/erro
    watch(() => questao.tipo_dado, () => {
      if (!requiresPontuacao.value) {
        questao.pontuacao = null;
        errors.pontuacao = "";
      }
    });

    onMounted(async () => {
      await fetchCategorias();
      const questaoId = route.params.id;

      if (questaoId) {
        try {
          const response = await axios.get(`/configuracoes/parametros-avaliacoes/questoes/${questaoId}`);
          Object.assign(questao, response.data);
          pontuacaoStr.value = toBR(questao.pontuacao);

          if (questao.id_categoria) {
            await fetchBlocos(questao.id_categoria);
            await fetchBlocosUsadosPorCategoria(questao.id_categoria);
            await fetchOpcoesDaQuestao(questaoId);
          }
        } catch (error) {
          console.error("Erro ao carregar a questão:", error);
        }
      } else {
        novoQuestao();
        pontuacaoStr.value = toBR(questao.pontuacao);
      }
    });

    return {
      questao,
      errors,
      isFormValid,
      isSubmitting,
      salvarQuestao,
      voltar,
      route,
      categoriasOptions,
      blocosOptions,
      tipoDadoOptions,
      onCategoriaChange,
      requiresPontuacao,
      addOpcao,
      removeOpcao,
      opcoes,
      pontuacaoStr,
      onPontuacaoInput,
      onPontuacaoBlur,
      isBlocoDisabled,
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.larger-field {
  min-width: 180px;
}

.form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }

</style>
